import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import ClientSidebar from "./components/ClientSidebar";
import { onTourImageError } from "@/lib/imageFallback";
import { useAuth } from "@/contexts/AuthContext";
import { BookingStatus, listMyBookings, type Booking } from "@/lib/bookings";
import { listMyFavorites, type Favorite } from "@/lib/favorites";

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";
}

export default function PerfilPage() {
  const { t, i18n } = useTranslation("profile");
  const navigate = useNavigate();
  const { user } = useAuth();
  const priceLocale = i18n.language === "es" ? "es-MX" : "en-US";

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([listMyBookings(), listMyFavorites()])
      .then(([b, f]) => {
        if (cancelled) return;
        if (b.status === "fulfilled") setBookings(b.value);
        if (f.status === "fulfilled") setFavorites(f.value);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Recent bookings first (most recent date), and total spent on settled bookings.
  const recentBookings = useMemo(
    () => [...bookings].sort((a, b) => b.date.localeCompare(a.date)),
    [bookings]
  );
  const totalSpent = useMemo(
    () => bookings
      .filter((b) => b.status === BookingStatus.Confirmed || b.status === BookingStatus.Completed)
      .reduce((sum, b) => sum + b.totalPrice, 0),
    [bookings]
  );

  const statusColor = (s: BookingStatus) => {
    if (s === BookingStatus.Confirmed) return "bg-ocean/10 text-ocean";
    if (s === BookingStatus.Pending) return "bg-sand/60 text-charcoal";
    if (s === BookingStatus.Completed) return "bg-gray-100 text-gray-600";
    return "bg-coral/10 text-coral";
  };

  const statusLabel = (s: BookingStatus) => {
    if (s === BookingStatus.Confirmed) return t("profile.statusConfirmed");
    if (s === BookingStatus.Pending) return t("profile.statusPending");
    if (s === BookingStatus.Completed) return t("profile.statusCompleted");
    return t("profile.statusCancelled");
  };

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ClientSidebar active="overview" />
            <div className="flex-1 min-w-0 space-y-6">
              {/* Profile card */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  <div className="w-20 h-20 rounded-full bg-ocean/10 flex items-center justify-center text-ocean text-2xl font-bold shrink-0">
                    {initials(user?.fullName ?? "")}
                  </div>
                  <div className="flex-1">
                    <h1 className="text-xl md:text-2xl font-bold text-charcoal">{user?.fullName ?? ""}</h1>
                    <p className="text-sm text-gray-500">{user?.email ?? ""}</p>
                  </div>
                  <button
                    onClick={() => navigate("/perfil/editar")}
                    className="border border-gray-200 text-charcoal text-sm font-medium px-4 py-2 rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    {t("profile.editProfile")}
                  </button>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: t("profile.statsBookings"), value: String(bookings.length), icon: "ri-calendar-check-line", color: "bg-ocean/10 text-ocean" },
                  { label: t("profile.statsFavorites"), value: String(favorites.length), icon: "ri-heart-3-line", color: "bg-coral/10 text-coral" },
                  { label: t("profile.statsSpent", { defaultValue: "Total" }), value: `$${totalSpent.toLocaleString(priceLocale)}`, icon: "ri-money-dollar-circle-line", color: "bg-turquoise/10 text-turquoise" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.color} mb-2`}>
                      <i className={`${s.icon} text-base`} />
                    </div>
                    <p className="text-lg font-bold text-charcoal">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Recent bookings */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold text-charcoal">{t("profile.recentBookings")}</h2>
                  <Link to="/perfil/reservas" className="text-sm text-ocean hover:underline">{t("profile.viewAll")}</Link>
                </div>
                <div className="space-y-4">
                  {loading ? (
                    Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className="h-24 bg-gray-50 rounded-xl animate-pulse" />
                    ))
                  ) : recentBookings.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">{t("profile.noBookings", { defaultValue: "Aún no tienes reservas." })}</p>
                  ) : (
                    recentBookings.slice(0, 2).map((b) => (
                      <div key={b.id} className="flex flex-col sm:flex-row gap-4 p-3 bg-gray-50 rounded-xl">
                        <img src={b.tour.coverImageUrl ?? undefined} alt={b.tour.title} onError={onTourImageError} className="w-full sm:w-28 h-20 object-cover rounded-lg shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-sm font-semibold text-charcoal truncate">{b.tour.title}</h3>
                            <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(b.status)}`}>
                              {statusLabel(b.status)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {b.date}{b.startTime ? ` ${b.startTime.slice(0, 5)}` : ""}</span>
                            <span className="flex items-center gap-1"><i className="ri-user-line" /> {b.adults + b.children} {t("profile.guests")}</span>
                            <span className="flex items-center gap-1"><i className="ri-money-dollar-circle-line" /> ${b.totalPrice.toLocaleString(priceLocale)} {b.currency}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Favorites preview */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold text-charcoal">{t("profile.favorites")}</h2>
                  <Link to="/perfil/favoritos" className="text-sm text-ocean hover:underline">{t("profile.viewAll")}</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse" />
                    ))
                  ) : favorites.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 sm:col-span-3">{t("profile.noFavorites", { defaultValue: "Aún no tienes favoritos." })}</p>
                  ) : (
                    favorites.slice(0, 3).map((fav) => (
                      <Link to={`/tours/${fav.slug}`} key={fav.tourId} className="group">
                        <div className="relative rounded-xl overflow-hidden mb-2">
                          <img src={fav.coverImageUrl ?? undefined} alt={fav.title} onError={onTourImageError} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />
                        </div>
                        <h3 className="text-sm font-semibold text-charcoal truncate">{fav.title}</h3>
                        <p className="text-xs text-gray-500">{fav.location}</p>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}